import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  Type,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'

import { filter, map } from 'rxjs'

import { FlxMessageThemeDirective, FlxThemeModule } from '@flowx/angular-theme'
import {
  FlxButtonComponent,
  FlxCardModule,
  FlxIconModule,
  FlxMessageComponent,
  FlxSpinnerComponent,
  FlxToastService,
  ToastData,
} from '@flowx/angular-ui-toolkit'
import {
  DEFAULT_LOCALE,
  ProcessSetupOptions,
  StartMode,
  UiFlowStartTrigger,
  addAsyncValidatorsToRegistry,
  addComponentsToRegistry,
  addInterceptors,
  addProcessEndFunc,
  addProcessStartFunc,
  addValidatorsToRegistry,
  canNavigateToTemplateConfig$,
  canPopSubprocess$,
  cleanupProcess,
  config,
  getActiveUiFlowSessionId,
  getCurrentReadOnlyState,
  getProcessInstanceUuidFromUrl,
  initProcess,
  navigateToTemplateConfig,
  popSubprocessFromNavigation,
  processNotificationsByActiveProcess$,
  readOnlyScreenTemplateConfig$,
  resolveAccessToken,
  screenTemplateConfig$,
  setAccessToken,
  setClientStore,
  setNomenclator,
  toasts$,
  uiStore$,
  urlUpdate$,
  warnDeprecatedOption,
} from '@flowx/core-sdk'
import { theme } from '@flowx/core-sdk'
import { ThemeComponent, getComponentStyles } from '@flowx/core-theme'

import { COMPONENT_WRAPPERS_MAP } from '../components/component.dictionaries'
import { FlxLocalizePipe } from '../pipes'
import { FlxTreeNodeComponent } from '../renderer/tree-node/tree-node.component'
import { FlxFormsService } from '../services/flx-forms.service'
import {
  FLX_CLIENT_STORE_CONFIG,
  FLX_COMPONENTS_CONFIG,
  FLX_CUSTOM_LOADER_CONFIG,
  FLX_NOMENCLATOR_CONFIG,
  FLX_REQUEST_INTERCEPTORS_CONFIG,
  FLX_RESPONSE_INTERCEPTORS_CONFIG,
  FLX_VALIDATORS_CONFIG,
} from '../services/providers'
import { resolveConnectionInputs } from '../utils/deprecated-inputs.utils'
import { stableProjection } from '../utils/stable-projection.utils'
import {
  FLX_ASYNC_VALIDATORS,
  FLX_VALIDATORS,
  NgAsyncValidator,
  NgSyncValidator,
} from '../utils/validators'

@Component({
  selector: 'flx-process-renderer',
  imports: [
    CommonModule,
    FlxTreeNodeComponent,
    FlxSpinnerComponent,
    FlxMessageComponent,
    FlxMessageThemeDirective,
    FlxLocalizePipe,
    FlxButtonComponent,
    FlxIconModule,
    FlxThemeModule,
    FlxCardModule,
  ],
  templateUrl: './process-renderer.component.html',
  styleUrl: './process-renderer.component.scss',
  host: { '[class.readonly]': `readonly()` },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxProcessRendererComponent implements OnInit, OnDestroy {
  #validators = inject(FLX_VALIDATORS_CONFIG, { optional: true })
  #customComponents = inject(FLX_COMPONENTS_CONFIG, { optional: true })
  #clientStore = inject(FLX_CLIENT_STORE_CONFIG, { optional: true })
  #nomenclatorService = inject(FLX_NOMENCLATOR_CONFIG, { optional: true })
  #requestInterceptors = inject(FLX_REQUEST_INTERCEPTORS_CONFIG, { optional: true })
  #responseInterceptors = inject(FLX_RESPONSE_INTERCEPTORS_CONFIG, { optional: true })
  #customLoader = inject(FLX_CUSTOM_LOADER_CONFIG, { optional: true })
  #destroyRef = inject(DestroyRef)
  #router = inject(Router)
  #route = inject(ActivatedRoute)
  #formService = inject(FlxFormsService)
  #toastService = inject(FlxToastService)

  baseUrl = input<string>()
  /** @deprecated Use `baseUrl` instead. Kept for backward compatibility; removal will be announced. */
  apiUrl = input<string>()
  accessToken = input<string>()
  /** @deprecated Use `accessToken` instead. Kept for backward compatibility; removal will be announced. */
  authToken = input<string>()
  enginePath = input<string>()
  /** @deprecated Use `enginePath` instead. Kept for backward compatibility; removal will be announced. */
  processApiPath = input<string>()
  processName = input<string>()
  resourceId = input<string>()
  projectInfo = input.required<{
    projectId: string
    appVersionId?: string
    selectedProcessResourceId?: string
  }>()
  assetsBaseUrl = input<string>()
  /** @deprecated Use `assetsBaseUrl` instead. Kept for backward compatibility; removal will be announced. */
  staticAssetsPath = input<string>()
  themeId = input.required<string>()

  language = input<string>()
  locale = input<string>(DEFAULT_LOCALE)

  buildId = input<string>()
  workspaceId = input<string>()
  organizationId = input.required<string>()
  processStartData = input<Record<string, any>>({})
  cache = input<boolean>(true)
  isDraft = input<boolean>(false)
  /** @deprecated No replacement; removal will be announced. */
  legacyHttpVersion = input<boolean>()
  startMode = input<StartMode>(StartMode.MANUAL)
  // Set by the UI-flow host to distinguish a process-renderer template ('template')
  // from a start_process UI action ('action'); forwarded to the start call for auditing.
  trigger = input<UiFlowStartTrigger>()

  processEnd = output<void>()
  processStart = output<string>()

  customLoader = computed<Type<any> | null>(() => {
    const { showLoader, loaderType } = this.loaderState$()

    if (showLoader === undefined || this.#customLoader === undefined) {
      return null
    }

    // custom loader for specific action
    if (this.#customLoader?.actions?.[showLoader]) {
      return this.#customLoader.actions?.[showLoader]
    }

    if (loaderType === 'action' && this.#customLoader?.defaultAction) {
      return this.#customLoader.defaultAction
    }

    if (loaderType === 'upload' && this.#customLoader?.defaultUpload) {
      return this.#customLoader.defaultUpload
    }

    return this.#customLoader?.[showLoader] ?? null
  })

  readonly = getCurrentReadOnlyState

  canNavigateToPreviousTemplateConfig = toSignal(canNavigateToTemplateConfig$(-1), {
    initialValue: false,
  })
  canNavigateToNextTemplateConfig = toSignal(canNavigateToTemplateConfig$(1), {
    initialValue: false,
  })
  canPopSubprocess = toSignal(canPopSubprocess$, {
    initialValue: false,
  })

  navigateToTemplateConfigFunc = navigateToTemplateConfig
  popSubprocessFromNavigationFunc = popSubprocessFromNavigation

  // screenTemplateConfig$ emits null for the whole in-place reload window (SSE
  // reloadProcess), unmounting the tree so the post-reload emission MOUNTS fresh
  // components. Without the blank, @for track keys collide (flowxUuids survive a
  // project migration), instances get reused, and their ngOnInit-time config
  // snapshots keep rendering the pre-migration screen.
  templateConfig$ = toSignal(
    screenTemplateConfig$
      .pipe
      // tap((t) => {
      //   if (t?.rootTemplates.templateConfig.length) {
      //     console.log('%c  - RENDER TEMPLATES :', 'color: cyan', t.rootTemplates.templateConfig)
      //   }
      //   if (t?.modalTemplates.templateConfig.length) {
      //     console.log('%c  - MODAL TEMPLATES :', 'color: cyan', t.modalTemplates.templateConfig)
      //   }
      // })
      ()
  )

  readonlyTemplateConfig$ = toSignal(readOnlyScreenTemplateConfig$)

  screenTemplateConfig$ = computed(() =>
    this.readonly() ? this.readonlyTemplateConfig$() : this.templateConfig$()
  )

  loaderState$ = toSignal(
    uiStore$.pipe(
      stableProjection(({ showLoader, loaderType, loaderText }) => ({
        showLoader,
        loaderType,
        loaderText,
      }))
    ),
    {
      initialValue: { showLoader: undefined, loaderType: undefined, loaderText: undefined },
    }
  )
  notifications$ = toSignal(processNotificationsByActiveProcess$())

  readonly COMPONENT_WRAPPERS_MAP = COMPONENT_WRAPPERS_MAP

  constructor() {
    effect(() => {
      const accessToken = resolveAccessToken({
        authToken: this.authToken(),
        accessToken: this.accessToken(),
      })
      if (!accessToken) {
        return
      }
      // Locked: a renderer initialized in anonymous mode never flips to authed.
      if (config<ProcessSetupOptions>()?.isAnonymousMode) {
        return
      }
      // Updates the global config and re-authenticates live SSE connections.
      setAccessToken(accessToken)
    })
  }

  ngOnInit(): void {
    // add custom validators to registry
    addValidatorsToRegistry<NgSyncValidator>({
      ...FLX_VALIDATORS,
      ...this.#validators,
    })
    addAsyncValidatorsToRegistry<NgAsyncValidator>(FLX_ASYNC_VALIDATORS)

    // add custom components to registry
    if (this.#customComponents) {
      addComponentsToRegistry<Type<any>>(this.#customComponents)
    }

    // add client store
    if (this.#clientStore) {
      setClientStore(this.#clientStore)
    }

    // add nomenclator service
    if (this.#nomenclatorService) {
      setNomenclator(this.#nomenclatorService)
    }
    // add interceptors; inside a UI flow the UI-flow renderer has registered them already
    if (getActiveUiFlowSessionId() === null) {
      addInterceptors({
        requestInterceptors: this.#requestInterceptors,
        responseInterceptors: this.#responseInterceptors,
      })
    }
    // add process ended handler
    addProcessEndFunc(() => this.processEnd.emit())
    // add process started handler
    addProcessStartFunc((processInstanceUuid) => this.processStart.emit(processInstanceUuid))

    initProcess({
      ...resolveConnectionInputs(this),
      language: this.language(),
      themeId: this.themeId(),
      processStartData: this.processStartData(),
      isDraft: this.isDraft(),
      processName: this.processName(),
      locale: this.locale(),
      // TODO: temporary until we refactor the application be apis as well. For the moment the change is only for the public api of the component
      appInfo: {
        appId: this.projectInfo()?.projectId,
        appVersionId: this.projectInfo()?.appVersionId,
        selectedProcessResourceId: this.projectInfo()?.selectedProcessResourceId,
      },
      resourceId: this.resourceId(),
      buildId: this.buildId(),
      workspaceId: this.workspaceId(),
      organizationId: this.organizationId(),
      cache: this.cache(),
      legacyHttpVersion: warnDeprecatedOption('legacyHttpVersion', this.legacyHttpVersion()),
      startMode: this.startMode(),
      trigger: this.trigger(),
      fw: 'angular',
    })

    // this is necessary because the process renderer does not update the processUuid query param through angular, so angular cannot detect the change
    urlUpdate$
      .pipe(
        map((url: URL) => ({
          processUuid: getProcessInstanceUuidFromUrl(url),
          uiFlowSessionUuid: url.searchParams.get('uiFlowSessionUuid'),
        })),
        filter(({ processUuid }) => !!processUuid),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ processUuid, uiFlowSessionUuid }) => {
        const queryParams: Record<string, string | null> = {
          processInstanceUuid: processUuid,
          processUuid: null,
        }
        if (uiFlowSessionUuid) {
          queryParams.uiFlowSessionUuid = uiFlowSessionUuid
        }
        this.#router.navigate([], {
          relativeTo: this.#route,
          queryParams,
          queryParamsHandling: 'merge',
          replaceUrl: true,
        })
      })

    // show toasts
    toasts$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((toast) => {
      const toastData: ToastData = { ...toast }
      if (toast.withTheme) {
        toastData.className = getComponentStyles(ThemeComponent.Message, theme()) || ''
      }
      this.#toastService.show(toastData)
    })
  }

  onNotificationActionClick(action: any): void {
    if (action?.url) {
      window.open(action.url, '_blank')
    } else if (action?.src) {
      const processConfig = config()
      window.open(`${processConfig.apiUrl}/documents/${action.src}`, '_blank')
    }
  }

  ngOnDestroy(): void {
    cleanupProcess()
    this.#formService.clearForms()
  }
}
